import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getGestockApiBaseUrl, getGestockStoreId } from "../config/gestock";
import { Product } from "../data/products";
import { formatPrice } from "../cart/cartUtils";

type CatalogStore = {
  id: string;
  name: string;
  contactName: string;
  whatsappNumber: string;
  instructions: string;
};

type CatalogContextValue = {
  products: Product[];
  store: CatalogStore;
  loading: boolean;
  error: string;
};

type GestockCatalogItem = {
  _id: string;
  name?: string;
  brand?: string;
  description?: string;
  category?: string;
  productCategory?: string;
  sellPrice?: number;
  quantityInStock?: number;
  imageUrl?: string;
  coverImageUrl?: string;
  imageUrls?: string[];
  createdAt?: string;
};

type GestockCatalogResponse = {
  store?: {
    _id?: string;
    name?: string;
    publicOrderContactName?: string;
    publicOrderWhatsappNumber?: string;
    publicOrderInstructions?: string;
  };
  items?: GestockCatalogItem[];
};

const FALLBACK_STORE: CatalogStore = {
  id: getGestockStoreId(),
  name: "Si'Bella Boutique",
  contactName: "",
  whatsappNumber: "2250757561119",
  instructions: "",
};

const CatalogContext = createContext<CatalogContextValue | undefined>(undefined);

function normalizeWhatsappNumber(value: string) {
  return String(value || "").replace(/[^\d]/g, "");
}

function inferCategory(item: GestockCatalogItem): Product["category"] {
  const haystack = [
    item.category,
    item.productCategory,
    item.name,
    item.brand,
    item.description,
  ]
    .join(" ")
    .toLowerCase();

  if (
    /(sac|bag|pochette|cabas|bandouli|handbag|shopper)/.test(haystack)
  ) {
    return "bags";
  }

  if (
    /(chauss|shoe|sandale|sandal|mule|talon|escarpin|basket|sneaker)/.test(
      haystack
    )
  ) {
    return "shoes";
  }

  return "bags";
}

function mapCatalogItems(items: GestockCatalogItem[]) {
  const sortedByDate = [...items].sort((a, b) => {
    const left = new Date(b.createdAt || 0).getTime();
    const right = new Date(a.createdAt || 0).getTime();
    return left - right;
  });

  const featuredIds = new Set(sortedByDate.slice(0, 6).map((item) => item._id));

  return items.map((item) => {
    const priceValue = Number(item.sellPrice || 0);
    const createdAt = item.createdAt ? new Date(item.createdAt).getTime() : 0;
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const primaryImage =
      item.imageUrl || item.coverImageUrl || item.imageUrls?.[0] || "";

    return {
      id: String(item._id),
      image: primaryImage,
      title: String(item.name || item.brand || "Produit"),
      price: formatPrice(priceValue),
      priceValue,
      category: inferCategory(item),
      isNew: createdAt >= thirtyDaysAgo,
      featured: featuredIds.has(item._id),
      stock: Number(item.quantityInStock || 0),
      description: String(item.description || "").trim(),
    } satisfies Product;
  });
}

export function CatalogProvider({ children }: { children: React.ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [store, setStore] = useState<CatalogStore>(FALLBACK_STORE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const storeId = getGestockStoreId();
    const endpoint = `${getGestockApiBaseUrl()}/public/stores/${storeId}/products/catalog`;

    async function loadCatalog() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(endpoint, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Catalog request failed with status ${response.status}`);
        }

        const payload = (await response.json()) as GestockCatalogResponse;
        const items = Array.isArray(payload.items) ? payload.items : [];

        setProducts(mapCatalogItems(items));
        setStore({
          id: String(payload.store?._id || storeId),
          name: String(payload.store?.name || FALLBACK_STORE.name),
          contactName: String(payload.store?.publicOrderContactName || ""),
          whatsappNumber:
            normalizeWhatsappNumber(payload.store?.publicOrderWhatsappNumber || "") ||
            FALLBACK_STORE.whatsappNumber,
          instructions: String(payload.store?.publicOrderInstructions || ""),
        });
      } catch (err) {
        if (controller.signal.aborted) return;

        console.error("Failed to load Gestock catalog:", err);
        setProducts([]);
        setStore(FALLBACK_STORE);
        setError("Impossible de charger le catalogue pour le moment.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadCatalog();

    return () => controller.abort();
  }, []);

  const value = useMemo(
    () => ({ products, store, loading, error }),
    [products, store, loading, error]
  );

  return (
    <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
  );
}

export function useCatalog() {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error("useCatalog must be used within CatalogProvider");
  }
  return context;
}
